import React, { useEffect, useState, useMemo } from "react";
import TextInput from "../../../components/TextInput";
import SearchButton from "../../../components/SearchButton";
import RefreshButton from "../../../components/RefreshButton";
import MultiSelectInput from "../../../components/MultiSelectInput";
import { BarsArrowUpIcon } from "@heroicons/react/24/solid";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { Disclosure } from "@headlessui/react";
import { useSFGetOffice } from "../OfficeSettings/hooks/useSFGetOffice";
import { accountAtom } from "../../Login/atoms/AccountAtom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import SelectInput from "../../../components/SelectInput";
import { useStates } from "../hooks/useStates";
import {
  defaultProdCodeSearchParams,
  franchiseFilterState,
  searchDataProducerCode,
} from "./atom";
import { useGetBilling } from "./hooks/useGetBilling";
import { useGetDistributionChannels } from "./hooks/useGetDashboardFilterOptions";

const SubTableFilter = ({
  handleSearchInputs,
  setFilterStatus,
  setFilteredproducerCodeData,
  oldFilteredValue,
  clearFilter,
  setClearFilter,
  mutationFilter,
  searchProducerCodeCriteriaEntry,
  setIsSubFilterLoading,
}) => {
  const user = useRecoilValue(accountAtom);
  const [producerCode, setProducerCode] = useState("");
  const [maProducerCode, setMAProducerCode] = useState("");
  const [AcquisitionName, setAcquisitionName] = useState("");
  const [searchInputsData, setSearchInputsData] = useRecoilState(
    searchDataProducerCode
  );
  const [, setSelectedFranchiseConfie] = useState([]);
  const setIsFranchiseFilter = useSetRecoilState(franchiseFilterState);

  const { data: agenciesQuery, isLoading } = useSFGetOffice();
  const agencies =
    agenciesQuery &&
    agenciesQuery.map((resData) => ({
      label:
        resData.officeName +
        "-" +
        resData.officeCode +
        "-" +
        (resData.storeNumber !== "" ? resData.storeNumber : "0"),
      value: resData.officeCode + "-" + resData.storeNumber,
      state: resData.stateCode,
      officeName: resData.officeName + "-" + resData.officeCode,
      officeCode: resData.officeCode,
    }));

  const { data: stateQuery } = useStates();
  const states =
    stateQuery &&
    stateQuery?.map((data) => {
      return { label: data.state, value: data.state };
    });

  const { data: billing } = useGetBilling();
  const billingOpt = billing?.map((x) => ({
    label: x.billingName,
    value: x.billingId,
  }));

  const franchiseOption = {
    label: "Franchise",
    value: -1,
  };

  const { data: distChannels, isLoading: channelLoading } =
    useGetDistributionChannels();
  const franchiseORConfie = [
    { label: "Confie", value: 1 },
    { label: "Franchise", value: 2 },
    { label: "Freeway Franchise", value: 3 },
  ];

  const distributionOpts = useMemo(() => {
    const channelOptions =
      distChannels?.map((x) => ({
        label: x.channelName,
        value: x.channelId,
      })) || [];

    const selected = searchInputsData?.selectedFranchiseConfie?.value;
    let allowedChannels: { label: string; value: number }[] = [];

    if (user.isFranchise && !user.isNonFranchise) {
      allowedChannels = [franchiseOption];
    } else if (!user.isFranchise && user.isNonFranchise) {
      allowedChannels = channelOptions;
    } else if (user.isFranchise && user.isNonFranchise) {
      allowedChannels = [...channelOptions, franchiseOption];
    }
    if (selected === 2 || selected === 3) {
      return [franchiseOption];
    }

    if (selected === 1) {
      return channelOptions;
    }
    return allowedChannels;
  }, [distChannels, user, searchInputsData?.selectedFranchiseConfie]);

  const statusOptions = ["Active", "Inactive", "Suspended"].map((status) => ({
    label: status,
    value: status.toLowerCase(),
  }));

  useEffect(() => {
    setProducerCode(searchInputsData.ProducerCode ?? "");
    setMAProducerCode(searchInputsData.MAProducerCode ?? "");
    setAcquisitionName(searchInputsData.AcquisitionName ?? "");
  }, [searchInputsData]);

  const refreshProducerCode = () => {
    setSearchInputsData(defaultProdCodeSearchParams);
    mutationFilter.mutate(defaultProdCodeSearchParams);
    setFilterStatus(false);
    setProducerCode("");
    setMAProducerCode("");
    setAcquisitionName("");
    handleSearchInputs("Reset", "");
    setSelectedFranchiseConfie([]);
    setFilteredproducerCodeData([]);
    setIsSubFilterLoading(false);
  };

  useEffect(() => {
    if (clearFilter) {
      refreshProducerCode();
      setClearFilter(false);
    }
  }, [clearFilter]);

  const handleSelection = (checkedValue, fieldName) => {
    if (fieldName === "ProducerCode") {
      oldFilteredValue.ProducerCode = checkedValue;
    }
    if (fieldName === "MAProducerCode") {
      oldFilteredValue.MAProducerCode = checkedValue;
    }
    if (fieldName === "AcquisitionName") {
      oldFilteredValue.AcquisitionName = checkedValue;
    }
  };

  const handleSelectMultiStates = (checkedStates) => {
    setSearchInputsData({
      ...searchInputsData,
      selectedState: checkedStates ? checkedStates : [],
      State: checkedStates ? checkedStates.map((x) => x.label) : [],
    });
  };

  const handleFranchiseConfieSelect = (selected) => {
    setSearchInputsData((prev) => ({
      ...prev,
      selectedFranchiseConfie: selected ?? null,
      DistributionChannelId: null,
      DistributionChannelName: null,
    }));
  };

  return (
    <div className="pl-4 space-y-2 pb-4 w-1/4 bg-white">
      <div>
        <BarsArrowUpIcon className="h-6 w-6 text-primary mb-1.5" />
        <div className="text-xl font-bold text-primary -mt-9 ml-8">
          Search Section
        </div>
      </div>
      <div className="w-full space-y-3 sm:w-40 md:w-40 lg:w-full">
        <div>
          <Disclosure defaultOpen>
            {({ open }) => (
              <>
                <Disclosure.Button className="sm:px-2 flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-primary bg-gray-100 rounded-t-lg hover:bg-gray-200 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75">
                  <span>Producer Code Details</span>
                  <ChevronDownIcon
                    className={`${
                      open && "transform rotate-180"
                    } w-5 h-5 text-gray-500`}
                  />
                </Disclosure.Button>
                <Disclosure.Panel className="bg-gray-100 text-sm text-primary rounded-b-lg">
                  <div className="w-full">
                    <div className="flex flex-col text-xs gap-y-2 p-3 sm:px-2 md:px-3 -mt-2">
                      {user.isFranchise && user.isNonFranchise ? (
                        <div className="w-full">
                          <label className="block font-medium text-gray-700 mb-2">
                            Franchise/Confie
                          </label>
                          <SelectInput
                            options={franchiseORConfie}
                            value={searchInputsData.selectedFranchiseConfie}
                            onChange={(options) => {
                              handleFranchiseConfieSelect(options);
                              setIsFranchiseFilter(true);
                            }}
                            className={undefined}
                            components={undefined}
                            field={undefined}
                            isLoading={undefined}
                          />
                        </div>
                      ) : (
                        <></>
                      )}

                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          States
                        </label>

                        <MultiSelectInput
                          options={states?.filter(
                            (v, i, a) =>
                              a.findIndex(
                                (t) =>
                                  t.label === v.label && t.value === v.value
                              ) === i
                          )}
                          value={searchInputsData.selectedState}
                          handleMultiSelect={(e) => {
                            if (!e || e.length === 0) {
                              refreshProducerCode();
                            } else {
                              handleSelectMultiStates(e);
                            }
                          }}
                          defaultValue={undefined}
                        />
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Offices
                        </label>

                        <MultiSelectInput
                          isDisabled={isLoading}
                          options={agencies?.filter(
                            (v, i, a) =>
                              a.findIndex(
                                (t) =>
                                  t.label === v.label && t.value === v.value
                              ) === i
                          )}
                          value={searchInputsData.selectedOffice}
                          handleMultiSelect={(e) => {
                            setSearchInputsData({
                              ...searchInputsData,
                              OfficeCode: e.map((x) => x.officeCode),
                              OfficeName: e.map((x) => x.label),
                              selectedOffice: e,
                            });
                          }}
                          defaultValue={undefined}
                        />
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          {"Producer Code/Princeton Code"}
                        </label>

                        <TextInput
                          name="ProducerCode"
                          value={producerCode}
                          onChange={(e) => {
                            setProducerCode(e.target.value);
                            handleSearchInputs("ProducerCode", e.target.value);
                            handleSelection(e.target.value, "ProducerCode");
                          }}
                          className="mr-18 outline-none focus:ring-septenary focus:border-septenary"
                        />
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          {"M&A Producer Code"}
                        </label>

                        <TextInput
                          name="MAProducerCode"
                          value={maProducerCode}
                          onChange={(e) => {
                            setMAProducerCode(e.target.value);
                            handleSearchInputs(
                              "MAProducerCode",
                              e.target.value
                            );
                            handleSelection(e.target.value, "MAProducerCode");
                          }}
                          className="mr-18 outline-none focus:ring-septenary focus:border-septenary"
                        />
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          {"Acquisition Name"}
                        </label>

                        <TextInput
                          name="acquisitionName"
                          value={AcquisitionName}
                          onChange={(e) => {
                            setAcquisitionName(e.target.value);
                            handleSearchInputs(
                              "AcquisitionName",
                              e.target.value
                            );
                            handleSelection(e.target.value, "AcquisitionName");
                          }}
                          className="mr-18 outline-none focus:ring-septenary focus:border-septenary"
                        />
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Billing
                        </label>
                        <div className="w-full">
                          <SelectInput
                            options={billingOpt}
                            value={searchInputsData.BillingName}
                            onChange={(e) => {
                              setSearchInputsData({
                                ...searchInputsData,
                                BillingName: e ? e : "",
                                BillingId: e ? e.value : null,
                              });
                            }}
                            className={undefined}
                            components={undefined}
                            field={undefined}
                            isLoading={undefined}
                          />
                        </div>
                      </div>
                      <div className="w-full">
                        <div className="block font-medium text-gray-700 mb-2">
                          Distribution Channel
                        </div>
                        <div className="mt-2">
                          <SelectInput
                            options={distributionOpts}
                            value={searchInputsData.DistributionChannelName}
                            onChange={(e) => {
                              setSearchInputsData((prev) => ({
                                ...prev,
                                DistributionChannelName: e ?? "",
                                DistributionChannelId: e?.value ?? null,
                              }));
                            }}
                            className={undefined}
                            components={undefined}
                            field={undefined}
                            isLoading={channelLoading}
                          />
                        </div>
                      </div>
                      <div className="w-full">
                        <div className="block font-medium text-gray-700 mb-2">
                          Producer Code Status
                        </div>
                        <div className="mt-2">
                          <MultiSelectInput
                            options={statusOptions}
                            value={searchInputsData.selectedStatus}
                            handleMultiSelect={(e) => {
                              setSearchInputsData({
                                ...searchInputsData,
                                selectedStatus: e || [],
                                Status: e?.map((status) => status.value) ?? [],
                              });
                            }}
                            defaultValue={undefined}
                          />
                        </div>
                      </div>
                      <div className="place-self-center">
                        <div className="flex flex-row gap-3 justify-center m-3">
                          <SearchButton
                            buttonValue="Search"
                            onClick={() => {
                              searchProducerCodeCriteriaEntry();
                            }}
                          />
                          <RefreshButton
                            onClick={() => {
                              refreshProducerCode();
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>
      </div>{" "}
    </div>
  );
};

export default SubTableFilter;
